import SPListService, {
  IGetItemsRequest,
  ISPListService,
} from "../base/sp-list.service";
import SPWebService, { ISPWebService } from "../base/sp-web.service";
import { IGraphService, GraphService } from "../base/graph.service";
import { IBaseService } from "../base/base.service";
import { Enums, Constants, Utilities, String } from "@utils";
import { IBaseLangMaster, IAEAColumnDefinition, IConsecutive } from "@models/";
import SPFileService, { ISPFileService } from "@services/base/sp-file.service";

export interface IBaseProyectaService {
  spListService: ISPListService;
  graphService: IGraphService;
  spWebService: ISPWebService;

  getRegions(langLabel: string): Promise<any>;
  getSLEP(langLabel: string): Promise<any>;
  getCountries(langLabel: string): Promise<any>;
  getBudgetTypes(langLabel: string): Promise<any>;
  getYears(langLabel: string): Promise<any>;
  getGrantCodes(langLabel: string): Promise<any>;
  getStrategicLines(langLabel: string): Promise<any>;
  getInterventionLines(langLabel: string): Promise<any>;
  getNatureExpenditures(langLabel: string): Promise<any>;
  getSAPFinancingEntities(langLabel: string): Promise<any>;
  getCECOS(langLabel: string): Promise<any>;
  getCampaigns(langLabel: string): Promise<any>;
  getPurposes(langLabel: string): Promise<any>;
  getDepartments(langLabel: string): Promise<any>;
  getFFCCFinancingEntities(langLabel: string): Promise<any>;
  getFFRRFinancingEntities(langLabel: string): Promise<any>;
  getFoundTypes(langLabel: string): Promise<any>;
  getConsecutive(key: string): Promise<IConsecutive>;
  getTagsProjec(key: string): Promise<any>;
  getcollective(key: string): Promise<any>;
  getSedeOn(langLabel: string): Promise<any>;
  getGaps(langLabel: string): Promise<any>;
  getIntermediateResult(langLabel: string): Promise<any>;
  getProductsIndicator(langLabel: string): Promise<any>;
  getAdminList(langLabel: string): Promise<any>;
  getAminSecitions(langLabel: string): Promise<any>;
  getApplications(): Promise<any>;
}

export class BaseProyectaService implements IBaseProyectaService {
  public spListService: ISPListService;
  public spFileService: ISPFileService;
  public spWebService: ISPWebService;
  public graphService: IGraphService;
  protected params: IBaseService;

  private filterActiveTrue: string = "OCTAVIAActivo eq 1";

  constructor(params?: IBaseService) {
    this.spListService = new SPListService(params);
    this.spFileService = new SPFileService(params);
    this.spWebService = new SPWebService(params);
    this.graphService = new GraphService(params);
    this.params = params;
  }

  /* private methods */
  private mappMasterLanguage(
    items: IBaseLangMaster[],
    langLabel: string,
    sort: boolean = true
  ): IBaseLangMaster[] {
    let _items = items.map((item) => {
      const mappItem = { ...item };
      switch (langLabel.toLocaleUpperCase()) {
        case Enums.LangLabels.EN:
          {
            mappItem.Title = item.OCTAVIANombreEn;
          }
          break;
        case Enums.LangLabels.PT:
          {
            mappItem.Title = item.OCTAVIANombrePT;
          }
          break;
        case Enums.LangLabels.FR:
          {
            mappItem.Title = item.OCTAVIANombreFR;
          }
          break;
      }
      return mappItem;
    });

    if (!sort) return _items;

    return Utilities.sortByKey(_items, "Title");
  }
  private async getDefaultMaster(
    request: IGetItemsRequest,
    langLabel: string,
    sort: boolean = true
  ) {
    request.caching = true;
    const items = await this.spListService.getItems<any>(request);
    return this.mappMasterLanguage(items, langLabel, sort);
  }

  /* shared methods */
  async getConsecutive(key: string): Promise<IConsecutive> {
    const itemConsecutive = (
      await this.spListService.getItems<IConsecutive>({
        listName: Enums.ListTypes.consecutive,
        filter: String.Format(Constants.FiltersFormats.FILTER_BY_TITLE, key),
      })
    )[0];
    return itemConsecutive;
  }
  async setConsecutive(key: string): Promise<IConsecutive> {
    const itemConsecutive = await this.spListService.addItem({
      listName: Enums.ListTypes.consecutive,
      item: {
        Title: key,
        PContador: 0,
        PIncremento: 1,
        PMaxNumLenth: 2,
      },
    });
    if (!Utilities.isNullObject(itemConsecutive)) {
      return itemConsecutive;
    }
    throw new Error(`consecutive ${key} not exist in this site`);
  }
  async getFoundTypes(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.FoundType, filter: this.filterActiveTrue },
      langLabel
    );
  }
  async getDefinitionColumns(langId: number, listName: string) {
    const columns = await this.spListService.getItems<IAEAColumnDefinition>({
      listName: listName,
      filter: String.Format(
        Constants.FiltersFormats.LANGUAGE_FILTER_BY_ID,
        langId
      ),
      caching: true,
    });
    return Utilities.mappArrayToDictionary<string, IAEAColumnDefinition>(
      Enums.Columns.Title,
      columns
    );
  }
  async getFFCCFinancingEntities(langLabel: string): Promise<any> {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.SAPFinancingEntity,
      filter: `${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title} eq '${Enums.FoundType.FFCC}'`,
      columnsToSelect: [
        `${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title}`,
        "*",
      ],
      expands: [`${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title}`],
      caching: true,
    });
    return this.mappMasterLanguage(items, langLabel);
  }
  async getFFRRFinancingEntities(langLabel: string): Promise<any> {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.SAPFinancingEntity,
      filter: `${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title} eq '${Enums.FoundType.FFRR}'`,
      columnsToSelect: [
        `${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title}`,
        "*",
      ],
      expands: [`${Enums.Columns.PPresTipoFondo}/${Enums.Columns.Title}`],
      caching: true,
    });
    return this.mappMasterLanguage(items, langLabel);
  }
  async getSLEP(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.SLEP,
        order: Constants.Orders.TITLE,
        ascending: true,
      },
      langLabel
    );
  }
  async getRegions(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.Region,
        order: Constants.Orders.TITLE,
        filter: this.filterActiveTrue,
        ascending: true,
      },
      langLabel
    );
  }
  async getCountries(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.Country,
        order: Constants.Orders.TITLE,
        ascending: true,
      },
      langLabel
    );
  }
  async getBudgetTypes(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.BudgetType, filter: this.filterActiveTrue },
      langLabel,
      false
    );
  }
  async getCounterpartType(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.ProaTipoContrapartida },
      langLabel,
      false
    );
  }
  async getMeasureUnits(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { 
        listName: Enums.ListTypes.MeasureUnits,
      },
      langLabel,
      true
    );
  }
  async getYears(): Promise<any> {
    let years = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.Year,
      caching: true,
    });
    return Utilities.sortByKey(years, "Title");
  }
  async getGrantCodes(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.GrantCode,
        expands: Constants.Expands.BUDGETCODEPROY,
        columnsToSelect: Constants.Selects.BUDGETCODEPROY,
      },
      langLabel
    );
  }
  async getStrategicLines(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.StrategicLine,
      },
      langLabel
    );
  }
  async getInterventionLines(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.InterventionLine,
        filter: this.filterActiveTrue,
      },
      langLabel,
      false
    );
  }
  async getNatureExpenditures(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.NatureExpenditure,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getSAPFinancingEntities(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.SAPFinancingEntity,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getCECOS(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.CECO,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getCampaigns(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.Campaign,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getPurposes(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.Purpose,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getDepartments(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.Department,
        order: Constants.Orders.TITLE,
      },
      langLabel
    );
  }
  async getProjectCode(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.projectCode,
        order: Constants.Orders.TITLE,
        ascending: true,
      },
      langLabel
    );
  }
  async getTagsProjec(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.Sectores, filter: this.filterActiveTrue },
      langLabel
    );
  }
  async getTypeRRHH(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.TypeRRHH, filter: this.filterActiveTrue },
      langLabel
    );
  }
  async getcollective(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.Collective, filter: this.filterActiveTrue },
      langLabel
    );
  }
  async getSedeOn(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.PresupuestoSedeOn,
      },
      langLabel
    );
  }
  async getCostRecovery(langLabel: string): Promise<any> {
    let costRecovery = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.ProaCostRecovery,
    });
    return costRecovery;
  }
  async getGaps(langLabel: string): Promise<any> {
    return this.getDefaultMaster({ listName: Enums.ListTypes.Gaps }, langLabel);
  }
  async getProaLocalization(): Promise<IBaseLangMaster[]> {
    let locations = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.ProaLocalization,
    });
    return Utilities.sortByKey(locations, "Title");
  }
  async getProaAdts(langLabel: string): Promise<IBaseLangMaster[]> {
    return this.getDefaultMaster(
      { listName: Enums.ListTypes.ProaAdt },
      langLabel
    );
  }

  async getIntermediateResult(langLabel: string) {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.IntermediateResult,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getProducts(langLabel: string) {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.Products,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getProductsIndicator(langLabel: string) {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.ProductsIndicator,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getIntermediateResultIndicator(langLabel: string) {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.IntermediateIndicatorResults,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getFollowupState(langLabel: string): Promise<any> {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.FollowupState,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getFollowupDimensions(langLabel: string): Promise<any> {
    const items = await this.spListService.getItems<any>({
      listName: Enums.ListTypes.FollowupDimensions,
    });
    return this.mappMasterLanguage(items, langLabel);
  }

  async getAdminList(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.GlobalAdministration,
        order: Constants.Orders.TITLE,
        filter: this.filterActiveTrue,
        ascending: true,
      },
      langLabel
    );
  }
  async getAminSecitions(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.AdminSections,
        order: Constants.Orders.TITLE,
        filter: this.filterActiveTrue,
        ascending: true,
      },
      langLabel
    );
  }
  async getApplications(): Promise<any> {
    return this.spListService.getItems({
      listName: Enums.ListTypes.Applications,
      order: Constants.Orders.ORDER,
      ascending: true,
    });
  }
  async getSuccessCriteria(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { 
        listName: Enums.ListTypes.SuccessCriteria,
        filter: this.filterActiveTrue,
      },
      langLabel,
      true
    );
  }
  async getFrecuency(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      { 
        listName: Enums.ListTypes.Frecuency,
        filter: this.filterActiveTrue,
      },
      langLabel,
      true
    );
  }
  async getAgeRange(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.FollowupAgeRange,
        order: Constants.Orders.ORDER,
        ascending: true,
      },
      langLabel, 
      false
    );
  }
  async getRuralities(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.FollowupRuralities,
        ascending: true,
      },
      langLabel
    );
  }
  async getInPeriods(langLabel: string): Promise<any> {
    return this.getDefaultMaster(
      {
        listName: Enums.ListTypes.SeguimientoInPeriodos,
        filter: this.filterActiveTrue,
        order: Constants.Orders.ORDER,
        ascending: true,
      },
      langLabel, 
      false
    );
  }
}
